import { Policy, PolicyType } from '../types/policy.types';
import { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface UsePolicyNewLogicProps {
	saveNewPolicy: (newPolicy: Policy) => Promise<Policy | undefined>;
	addTemporaryPolicy: (policy: Policy) => void;
	updateTemporaryPolicy: (policyId: string, updates: Partial<Policy>) => void;
	removeTemporaryPolicy: (policyId: string) => void;
	handleTogglePolicy: (policyId: string, isActive: boolean) => Promise<void>;
	selectPolicy: (policy: Policy) => void;
}

const generateTempId = () => `temp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

const createNewPolicyTemplate = (policyType: PolicyType): Policy => ({
	_id: generateTempId(),
	policyType,
	title: 'Nova Política',
	description: 'Preencha a descrição rápida da nova política aqui',
	versionNumber: '1.0',
	content: 'Preencha o conteúdo da nova política...',
	effectiveDate: new Date().toLocaleDateString('pt-BR'),
	isActive: false,
	updatedAt: new Date().toLocaleString('pt-BR'),
});

export const usePolicyNewLogic = ({
	saveNewPolicy,
	addTemporaryPolicy,
	updateTemporaryPolicy,
	removeTemporaryPolicy,
	handleTogglePolicy,
	selectPolicy,
}: UsePolicyNewLogicProps) => {
	const navigate = useNavigate();

	const [selectedType, setSelectedType] = useState<PolicyType | null>(null);
	const [newPolicy, setNewPolicy] = useState<Policy | null>(null);
	const [isPreviewMode, setIsPreviewMode] = useState(false);
	const isAddedRef = useRef(false);
	const currentPolicyIdRef = useRef<string | null>(null);
	const isSavingRef = useRef(false);

	useEffect(() => {
		if (newPolicy && !isAddedRef.current) {
			addTemporaryPolicy(newPolicy);
			isAddedRef.current = true;
			currentPolicyIdRef.current = newPolicy._id;
		}
	}, [newPolicy, addTemporaryPolicy]);

	useEffect(() => {
		return () => {
			if (isAddedRef.current && currentPolicyIdRef.current && !isSavingRef.current) {
				if (currentPolicyIdRef.current.startsWith('temp-')) {
					removeTemporaryPolicy(currentPolicyIdRef.current);
				}
			}
		};
	}, [removeTemporaryPolicy]);

	useEffect(() => {
		if (isAddedRef.current && newPolicy) {
			updateTemporaryPolicy(newPolicy._id, newPolicy);
		}
	}, [newPolicy, updateTemporaryPolicy]);

	const handleSave = useCallback(async () => {
		if (!newPolicy) return;
		if (isSavingRef.current) return;

		try {
			isSavingRef.current = true;

			const savedPolicy = await saveNewPolicy(newPolicy);

			if (savedPolicy) {
				if (currentPolicyIdRef.current?.startsWith('temp-')) {
					removeTemporaryPolicy(currentPolicyIdRef.current);
				}

				currentPolicyIdRef.current = savedPolicy._id;
				isAddedRef.current = false;

				navigate(`/politicas/${savedPolicy._id}`, { replace: true });
			} else {
				isSavingRef.current = false;
			}
		} catch (error) {
			isSavingRef.current = false;
			console.error('Falha ao salvar:', error);
		}
	}, [newPolicy, saveNewPolicy, navigate, removeTemporaryPolicy]);

	const handleVisualize = useCallback(() => {
		setIsPreviewMode((prev) => !prev);
	}, []);

	const handlePolicyChange = useCallback((updatedFields: Partial<Policy>) => {
		setNewPolicy((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				...updatedFields,
				lastEditedAt: new Date().toLocaleString('pt-BR'),
			};
		});
	}, []);

	const handleAddPolicy = useCallback(() => {
		if (
			isAddedRef.current &&
			currentPolicyIdRef.current &&
			currentPolicyIdRef.current.startsWith('temp-')
		) {
			removeTemporaryPolicy(currentPolicyIdRef.current);
		}

		setNewPolicy(null);
		setSelectedType(null);
		setIsPreviewMode(false);
		isAddedRef.current = false;
		currentPolicyIdRef.current = null;
		isSavingRef.current = false;

		navigate('/politicas/nova');
	}, [removeTemporaryPolicy, navigate]);

	const handleToggle = useCallback(async (checked: boolean) => {
		setNewPolicy((prev) => {
			if (!prev) return prev;
			return { ...prev, isActive: checked };
		});
	}, []);

	const handleCardToggle = useCallback(
		(policyId: string, isActive: boolean) => {
			if (policyId === newPolicy?._id) {
				handleToggle(isActive);
			} else {
				handleTogglePolicy(policyId, isActive);
			}
		},
		[newPolicy, handleTogglePolicy, handleToggle]
	);

	const handleSelectPolicy = useCallback(
		(policy: Policy) => {
			selectPolicy(policy);
			navigate(`/politicas/${policy._id}`);
		},
		[selectPolicy, navigate]
	);

	const handleTypeSelect = useCallback((type: PolicyType) => {
		setSelectedType(type);
		const policy = createNewPolicyTemplate(type);
		setNewPolicy(policy);
		currentPolicyIdRef.current = policy._id;
		isSavingRef.current = false;
	}, []);

	return {
		selectedType,
		newPolicy,
		isPreviewMode,
		handleSave,
		handleVisualize,
		handlePolicyChange,
		handleAddPolicy,
		handleToggle,
		handleCardToggle,
		handleSelectPolicy,
		handleTypeSelect,
	};
};
